import { parseNum } from '@bihu/common-js'
import React from 'react'
import brandMark from '@/assets/application-flow-skins/shared/brand-mark-gold.png'
import codeIcon from '@/assets/application-flow-skins/new-purple/register/code-icon.svg'
import phoneIcon from '@/assets/application-flow-skins/new-purple/register/phone-icon.svg'
import jhjBrandSloganWhite from '@/assets/application-flow-skins/shared/jhj-brand-slogan-white.png'
import { ApplicationFlowHeader } from '../components'
import { RegisterSkinProps } from '../types'
import themeStyles from '../theme/application-flow-theme.module.scss'
import styles from './register-skin.module.scss'

const AMOUNT_OPTIONS = [
  {
    label: '￥10,000',
    value: 10000,
  },
  {
    label: '￥20,000',
    value: 20000,
  },
  {
    label: '全部借出',
    value: 200000,
  },
]
const PERIOD_OPTIONS = ['6个月', '9个月', '12个月']
const VERIFY_CODE_LENGTH = 6

/**
 * RegisterSkin — 申请流程注册页的纯展示皮肤
 * @returns {React.ReactElement} 注册页展示节点
 */
const RegisterSkin: React.FC<RegisterSkinProps> = React.memo(({
  model,
  theme,
  onChangeAmount,
  onChangeCode,
  onChangePhone,
  onChangePeriod,
  onBack,
  onCloseAgreementDialog,
  onCloseCodeDialog,
  onConfirmAgreement,
  onOpenProtocol,
  onRequestCode,
  onSubmit,
  onToggleAgreement,
  onValidatePhone,
}) => {
  let resendButtonText = '重新获取验证码'
  if (model.isSending) {
    resendButtonText = '发送中'
  } else if (model.remainingSeconds) {
    resendButtonText = `${model.remainingSeconds}s`
  }

  return (
    <main className={themeStyles.themeRoot} data-theme={theme.name}>
      <div className={styles.page}>
        <img alt="" aria-hidden="true" className={styles.cardHand} height="120" src={theme.registerCardHand} width="119" />
        <ApplicationFlowHeader onBack={onBack} title={model.brandName} />
        <section aria-label={model.brandName} className={styles.brandArea}>
          {model.isJhjBrand ? (
            <img alt="" aria-hidden="true" className={styles.brandSlogan} height="24" src={jhjBrandSloganWhite} width="228" />
          ) : (
            <>
              <img alt="" className={styles.brandMark} height="28" src={brandMark} width="28" />
              <div className={styles.brandTitleWrap}>
                <strong>{model.brandName}</strong>
                <span aria-hidden="true" className={styles.divider} />
                <span>正规品牌 真实可信</span>
              </div>
            </>
          )}
        </section>

        <section className={styles.card}>
          <p className={styles.limitHint}>最高可借200000元</p>
          <div className={styles.amountRow}>
            <span className={styles.amount} data-testid="selected-amount">
              <span className={styles.currency}>￥</span>
              <span className={styles.amountValue}>{parseNum(model.amount)}</span>
            </span>
            <span className={styles.editHint}>（可修改金额）</span>
          </div>
          <div className={styles.amountOptions}>
            {AMOUNT_OPTIONS.map(option => {
              const isSelected = model.amount === option.value
              return (
                <button
                  className={isSelected ? styles.amountSelectedOption : styles.amountOption}
                  key={option.value}
                  onClick={() => onChangeAmount(option.value)}
                  type="button"
                >
                  <span>{option.label}</span>
                  {isSelected ? <span aria-hidden="true" className={styles.checkBadge} /> : null}
                </button>
              )
            })}
          </div>
          <p className={styles.rate}>年化利率(单利)<span>7.2%-24%</span>，具体利率以审核为准</p>
          <h2 className={styles.sectionTitle}>借多久</h2>
          <div className={styles.periodOptions}>
            {PERIOD_OPTIONS.map(option => {
              const isSelected = model.period === option
              return (
                <button
                  className={isSelected ? styles.periodSelectedOption : styles.periodOption}
                  key={option}
                  onClick={() => onChangePeriod(option)}
                  type="button"
                >
                  <span>{option}</span>
                  {isSelected ? <span aria-hidden="true" className={styles.checkBadge} /> : null}
                </button>
              )
            })}
          </div>
          <label className={`${styles.phoneField} ${model.phoneError ? styles.inputError : ''}`}>
            <img alt="" aria-hidden="true" className={styles.phoneIcon} src={phoneIcon} />
            <input
              aria-invalid={Boolean(model.phoneError)}
              autoComplete="tel"
              inputMode="numeric"
              maxLength={11}
              name="mobile"
              onBlur={onValidatePhone}
              onChange={event => onChangePhone(event.target.value.replace(/\D/g, ''))}
              placeholder="请输入本人实名认证手机号"
              type="tel"
              value={model.phone}
            />
          </label>
          <p aria-live="polite" className={styles.error}>{model.phoneError}</p>
          <div className={styles.submitBlock}>
            <button className={styles.primaryButton} onClick={onRequestCode} type="button">去借钱</button>
            <div className={styles.agreement}>
              <button
                aria-label="同意注册及隐私协议"
                aria-pressed={model.checked}
                className={model.checked ? styles.checkedBox : styles.checkBox}
                onClick={onToggleAgreement}
                type="button"
              >
                {model.checked ? '✓' : ''}
              </button>
              <span>阅读并同意</span>
              <button onClick={() => onOpenProtocol('register')} type="button">《用户注册服务协议》</button>
              <span>和</span>
              <button onClick={() => onOpenProtocol('privacy')} type="button">《隐私政策》</button>
            </div>
          </div>
        </section>
        <footer className={styles.statement}>
          <div className={styles.statementTitle}>
            <span className={styles.line} />
            <strong>郑重声明</strong>
            <span className={styles.line} />
          </div>
          <p>本平台为金融信息服务平台</p>
          <p>所有贷款申请在未成功贷款前绝不收取任何费用</p>
          <p>为了保障您的资金安全</p>
          <p>请不要相信任何要求您支付费用短信、邮件、电话等不实信息</p>
          <p>年化利率(单利):7.2%-24%具体放款金额及放款时间视个人情况而定</p>
          <p>贷款有风险 申款需谨慎!</p>
          <p>粤ICP备17137794号 | 广州市聚富互联网小额贷款有限公司</p>
          <p>客服电话：400-887-9891(9:00-18:00)</p>
        </footer>

        {model.showAgreementDialog ? (
          <div aria-label="协议确认" aria-modal="true" className={styles.dialogMask} role="dialog">
            <section className={styles.dialogCard}>
              <h2>请阅读并同意协议</h2>
              <p>
                您点击“同意”，即表示您已阅读并同意
                <button onClick={() => onOpenProtocol('register')} type="button">《用户注册服务协议》</button>
                和
                <button onClick={() => onOpenProtocol('privacy')} type="button">《隐私政策》</button>
                的全部条款
              </p>
              <div className={styles.dialogActions}>
                <button className={styles.dialogSecondary} onClick={onCloseAgreementDialog} type="button">不同意</button>
                <button className={styles.dialogPrimary} onClick={onConfirmAgreement} type="button">同意</button>
              </div>
            </section>
          </div>
        ) : null}

        {model.showCodeDialog ? (
          <div className={styles.dialogMask} role="presentation">
            <section aria-labelledby="verify-code-title" aria-modal="true" className={styles.dialog} role="dialog">
              <button aria-label="关闭验证码弹窗" className={styles.dialogClose} onClick={onCloseCodeDialog} type="button">×</button>
              <h2 id="verify-code-title">输入验证码</h2>
              <label className={`${styles.codeField} ${model.codeError ? styles.inputError : ''}`}>
                <img alt="" aria-hidden="true" className={styles.phoneIcon} src={codeIcon} />
                <input
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  maxLength={VERIFY_CODE_LENGTH}
                  name="verifyCode"
                  onChange={event => onChangeCode(event.target.value.replace(/\D/g, ''))}
                  placeholder="请输入验证码"
                  spellCheck={false}
                  type="tel"
                  value={model.code}
                />
                <button disabled={model.isSending || model.remainingSeconds > 0} onClick={onRequestCode} type="button">
                  {resendButtonText}
                </button>
              </label>
              <p aria-live="polite" className={styles.error}>{model.codeError}</p>
              <button className={styles.primaryButton} disabled={model.isSubmitting} onClick={onSubmit} type="button">
                {model.isSubmitting ? '领取中…' : '获取额度'}
              </button>
            </section>
          </div>
        ) : null}
      </div>
    </main>
  )
})

RegisterSkin.displayName = 'RegisterSkin'

export default RegisterSkin
